import { Input } from '../ui/input';
import { CheckCircle } from 'lucide-react';

export function SKUGrid({ skus, dispatchEntries, jobCardEntries, brandEntries, rateEntries, updateDispatchQty, updateJobCard, updateBrand, updateRate, showPOColumn }) {
  const makeKey = (poId, sku, size) => `${poId}|||${sku}|||${size}`;

  return (
    <div className="border border-zinc-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm min-w-[1150px]">
        <thead className="bg-zinc-50">
          <tr>
            {showPOColumn && (
              <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'10%'}}>PO No.</th>
            )}
            <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width: showPOColumn ? '10%' : '12%'}}>SKU</th>
            <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'9%'}}>Brand</th>
            <th className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'5%'}}>Size</th>
            <th className="text-right p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'6%'}}>Balance</th>
            <th className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'11%'}}>Job Card *</th>
            <th className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'12%'}}>Qty</th>
            <th className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'9%'}}>Rate</th>
            <th className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'5%'}}>GST %</th>
            <th className="text-right p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'9%'}}>Tax Amt</th>
            <th className="text-right p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" style={{width:'9%'}}>Total</th>
          </tr>
        </thead>
        <tbody>
          {skus.map((sku, idx) => {
            const key = makeKey(sku.po_id, sku.sku, sku.size);
            const isComplete = sku.balance_qty === 0;
            const currentRate = parseFloat(rateEntries[key] || sku.rate) || 0;
            const currentQty = parseInt(dispatchEntries[key]) || 0;
            const gstPercent = currentRate < 2500 ? 5 : 18;
            const baseAmount = currentQty * currentRate;
            const taxAmount = baseAmount * (gstPercent / 100);
            const totalAmount = baseAmount + taxAmount;

            return (
              <tr key={key} className={`border-t border-zinc-200 ${idx % 2 === 1 ? 'bg-zinc-50/50' : ''} ${isComplete ? 'opacity-50' : ''}`}>
                {showPOColumn && (
                  <td className="p-2 font-mono text-xs text-zinc-600">{sku.po_number}</td>
                )}
                <td className="p-2 font-mono text-xs">{sku.sku}</td>
                <td className="p-2">
                  {!isComplete ? (
                    <Input type="text" value={brandEntries[key] || sku.brand || ''} onChange={(e) => updateBrand(sku.po_id, sku.sku, sku.size, e.target.value)} placeholder="Brand" className="h-7 rounded-sm text-xs w-full" data-testid={`brand-${sku.sku}-${sku.size}`} />
                  ) : <span className="text-xs text-zinc-400">{sku.brand || '-'}</span>}
                </td>
                <td className="p-2 text-center font-mono text-xs">{sku.size}</td>
                <td className="p-2 text-right font-mono text-xs font-medium">
                  {isComplete ? (
                    <span className="text-green-600 flex items-center justify-end gap-1"><CheckCircle size={10} /> Done</span>
                  ) : sku.balance_qty}
                </td>
                <td className="p-2">
                  {!isComplete && (
                    <Input type="text" value={jobCardEntries[key] || ''} onChange={(e) => updateJobCard(sku.po_id, sku.sku, sku.size, e.target.value)} placeholder="JC-001 *" className={`h-7 rounded-sm font-mono text-xs w-full ${currentQty > 0 && !jobCardEntries[key]?.trim() ? 'border-red-400 bg-red-50' : ''}`} data-testid={`job-card-${sku.sku}-${sku.size}`} />
                  )}
                </td>
                <td className="p-2">
                  {!isComplete && (
                    <Input type="number" min="0" max={sku.balance_qty} value={dispatchEntries[key] || ''} onChange={(e) => updateDispatchQty(sku.po_id, sku.sku, sku.size, e.target.value)} placeholder="0" className="h-7 rounded-sm font-mono text-center w-full" data-testid={`dispatch-qty-${sku.sku}-${sku.size}`} />
                  )}
                </td>
                <td className="p-2">
                  {!isComplete && (
                    <Input type="number" step="0.01" min="0" value={rateEntries[key] || sku.rate || ''} onChange={(e) => updateRate(sku.po_id, sku.sku, sku.size, e.target.value)} placeholder="0.00" className="h-7 rounded-sm font-mono text-center w-full" data-testid={`rate-${sku.sku}-${sku.size}`} />
                  )}
                </td>
                <td className="p-2 text-center font-mono text-xs font-medium" data-testid={`gst-${sku.sku}-${sku.size}`}>
                  {!isComplete && currentRate > 0 ? (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${gstPercent === 5 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{gstPercent}%</span>
                  ) : null}
                </td>
                <td className="p-2 text-right font-mono text-xs">{!isComplete && currentQty > 0 ? taxAmount.toFixed(2) : ''}</td>
                <td className="p-2 text-right font-mono text-xs font-medium">{!isComplete && currentQty > 0 ? totalAmount.toFixed(2) : ''}</td>
              </tr>
            );
          })}
        </tbody>
        {(() => {
          let sumQty = 0, sumBase = 0, sumTax = 0, sumTotal = 0;
          skus.forEach(sku => {
            const key = makeKey(sku.po_id, sku.sku, sku.size);
            const q = parseInt(dispatchEntries[key]) || 0;
            const r = parseFloat(rateEntries[key] || sku.rate) || 0;
            const gst = r < 2500 ? 5 : 18;
            const base = q * r;
            const tax = base * (gst / 100);
            sumQty += q; sumBase += base; sumTax += tax; sumTotal += base + tax;
          });
          if (sumQty === 0) return null;
          return (
            <tfoot className="bg-zinc-100 border-t-2 border-zinc-300">
              <tr>
                <td colSpan={showPOColumn ? 6 : 5} className="p-2 text-xs font-bold text-right uppercase tracking-wider text-zinc-600">Totals</td>
                <td className="p-2 text-center font-mono text-xs font-bold" data-testid="total-dispatch-qty">{sumQty}</td>
                <td className="p-2 text-right font-mono text-xs font-bold">{sumBase.toFixed(2)}</td>
                <td className="p-2"></td>
                <td className="p-2 text-right font-mono text-xs font-bold">{sumTax.toFixed(2)}</td>
                <td className="p-2 text-right font-mono text-xs font-bold">{sumTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          );
        })()}
      </table>
    </div>
  );
}
